import React from 'react';
import { APP_MODAL_ROUTES } from '@/navigators/routes';
import { AppModalStackParams } from '@/navigators/types';
import { BaseActionSheet } from '@/components/base-action-sheet';
import {
  PaymentReviewAction,
  PaymentReviewComponent,
} from '@/components/payments/payment-review-component';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useNavigation } from '@react-navigation/native';
import { usePaymentsContext } from '@/contexts/payments-context';

type Navigation = StackNavigationProp<
  AppModalStackParams,
  APP_MODAL_ROUTES.ViewPADAgreement
>;

export interface Props {
  additionalActions?: PaymentReviewAction[];
  calculatedTransactionFee: number;
  checkNumber?: string;
  isCash?: boolean;
  isKlarnaBusy?: boolean;
  isManualCheck?: boolean;
  isSubmitting: boolean;
  onChange: () => void;
  onChangeCheckNumber?: (value: string) => void;
  onClose: () => void;
  onCollectPADConsent: () => void;
  onKlarnaContinue?: () => Promise<boolean>;
  onSubmit: () => Promise<void>;
  payAmountInMicrosOverride?: number | null;
  reviewBanner?: React.ReactNode;
  subtotalInMicros: number;
  transactionFeeName: string | null;
}

export const PaymentReviewActionSheet: React.FC<Props> = ({
  additionalActions,
  calculatedTransactionFee,
  checkNumber,
  isCash,
  isKlarnaBusy,
  isManualCheck,
  isSubmitting,
  onChangeCheckNumber,
  onChange,
  onClose,
  onCollectPADConsent,
  onKlarnaContinue,
  onSubmit,
  payAmountInMicrosOverride,
  reviewBanner,
  subtotalInMicros,
  transactionFeeName,
}) => {
  const navigation = useNavigation<Navigation>();
  const { selectedPaymentMethod, setPaymentContextState } =
    usePaymentsContext();
  const { selectedRestaurantRelationship, selectedRestaurantUUID } =
    useAuthenticatedUserContext();

  return (
    <BaseActionSheet
      isVisible
      onClose={() => {
        // Prevent closing while payment is being submitted
        if (isSubmitting) {
          return;
        }

        onClose();
      }}
    >
      <PaymentReviewComponent
        additionalActions={additionalActions}
        calculatedTransactionFee={calculatedTransactionFee}
        checkNumber={checkNumber}
        isCash={isCash}
        isKlarnaBusy={isKlarnaBusy}
        isManualCheck={isManualCheck}
        isSubmitting={isSubmitting}
        onChange={onChange}
        onChangeCheckNumber={onChangeCheckNumber}
        onCollectPADConsent={onCollectPADConsent}
        onKlarnaContinue={
          onKlarnaContinue
            ? async () => {
                const succeeded = await onKlarnaContinue();
                if (!succeeded) {
                  onClose();
                }
                return succeeded;
              }
            : undefined
        }
        onSubmit={onSubmit}
        onViewPADAgreement={() => {
          if (!selectedPaymentMethod) {
            return;
          }

          // Reopen review action sheet when navigating back
          navigation.navigate(APP_MODAL_ROUTES.ViewPADAgreement, {
            paymentMethodUUID: selectedPaymentMethod.uuid,
            restaurantUUID: selectedRestaurantUUID,
            supplierUUID: selectedRestaurantRelationship.supplierUUID,
          });
          onClose();
          setPaymentContextState({ autoShowModal: true });
        }}
        payAmountInMicrosOverride={payAmountInMicrosOverride}
        reviewBanner={reviewBanner}
        subtotalInMicros={subtotalInMicros}
        transactionFeeName={transactionFeeName}
      />
    </BaseActionSheet>
  );
};
